import React, { useState, useEffect } from 'react';
import { TextField, MenuItem } from '@material-ui/core';
import classNames from 'classnames';
import { useField, useFormikContext } from 'formik';
import useDebounce from '@forvalter/utils/useDebounce';
import { useGetDynamicFormDataQuery } from '../../services/rtkApi';
import { useFormContext } from '@klp/formgenerator';

interface FormDropdownProps {
    id: string;
    label: string;
    apiProperty?: string;
    isRequired: boolean;
    isReadOnly: boolean;
    classes: {
        editableField: string;
        readOnlyField: string;
    };
    defaultValue?: string;
    value?: string;
    isHidden?: boolean;

    fallbackItems?: Array<{ id: string; label: string }>;
}

export const FormDropdown: React.FC<FormDropdownProps> = ({
    id,
    label,
    apiProperty,
    isRequired,
    isReadOnly,
    classes,
    defaultValue,
    value,
    isHidden,
    fallbackItems = [],
}) => {
    const { values, setFieldValue, handleBlur } =
        useFormikContext<Record<string, unknown>>();
    const fieldValue = values[id] as string;
    const [formikField, meta] = useField(id);
    const { regionContext } = useFormContext();
    const [shouldIncludeRegion, setShouldIncludeRegion] = useState(true);

    // Local state for immediate UI updates
    const [localValue, setLocalValue] = useState(fieldValue ?? '');

    // Use debounced value for Formik updates
    const { debouncedValue } = useDebounce(localValue, 300);

    // Update local state when field value changes from outside
    useEffect(() => {
        setLocalValue(fieldValue ?? '');
    }, [fieldValue]);

    // Update Formik when debounced value changes
    useEffect(() => {
        if (debouncedValue !== fieldValue) {
            setFieldValue(id, debouncedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(event.target.value);
    };

    const handleFieldBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // Immediately sync on blur to ensure Formik is up to date
        setFieldValue(id, localValue);
        handleBlur(event);
    };

    // Fetch API data if apiProperty is provided
    const { data } = useGetDynamicFormDataQuery(
        {
            endpoint: apiProperty!,
            params: shouldIncludeRegion ? { region: regionContext } : {},
        },
        {
            skip: !apiProperty || isHidden,
        },
    );

    // Handle case where no data is returned for the given region. Try again without region.
    useEffect(() => {
        if (!data || !regionContext) return;

        if (data.length === 0 && shouldIncludeRegion) {
            setShouldIncludeRegion(false);
        }
    }, [data, shouldIncludeRegion, regionContext]);

    return (
        <TextField
            {...formikField}
            select={true}
            fullWidth={true}
            className={classNames({
                [classes.editableField]: !isReadOnly,
                [classes.readOnlyField]: isReadOnly,
            })}
            required={isRequired}
            variant="outlined"
            name={id}
            value={localValue}
            onChange={handleChange}
            onBlur={handleFieldBlur}
            id={id}
            defaultValue={value ?? defaultValue}
            disabled={isReadOnly}
            InputLabelProps={{
                shrink: true,
            }}
            label={label}
            error={meta.touched && Boolean(meta.error)}
            helperText={meta.touched ? meta.error : ''}
        >
            {data && apiProperty
                ? (data?.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                          {v.value}
                      </MenuItem>
                  )) ?? <MenuItem key={undefined} value={undefined} />)
                : fallbackItems?.map(({ id: itemId, label: itemLabel }) => (
                      <MenuItem key={String(itemId)} value={String(itemId)}>
                          {itemLabel}
                      </MenuItem>
                  ))}
        </TextField>
    );
};
